import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { GraduationCap, Plus, Search } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";

// Page « Mes élèves » (enseignant) : recherche, création de compte élève, accès aux fiches privées
export default function ElevesPage() {
  const { t } = useAuth();
  const navigate = useNavigate();
  const [students, setStudents] = useState([]);
  const [search, setSearch] = useState("");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ prenom: "", nom: "", instrument: "", level: "" });
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/teaching/students").then((r) => setStudents(r.data.students)).catch(() => {});
  useEffect(() => { load(); }, []);

  const create = async () => {
    setSaving(true);
    try {
      const r = await api.post("/teaching/students", form);
      toast.success(t("teach.student_created", { login: r.data.login_id }), { duration: 12000 });
      setOpen(false);
      setForm({ prenom: "", nom: "", instrument: "", level: "" });
      load();
    } catch (e) { toast.error(e.friendly); }
    setSaving(false);
  };

  const q = search.trim().toLowerCase();
  const filtered = q
    ? students.filter((s) => `${s.prenom} ${s.nom}`.toLowerCase().includes(q))
    : students;

  return (
    <div className="space-y-4" data-testid="teach-students-page">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <h2 className="font-heading text-2xl font-bold flex items-center gap-2">
          <GraduationCap className="h-6 w-6 brand" /> {t("teach.students_title")}
        </h2>
        <Button className="brand-bg text-white" onClick={() => setOpen(true)} data-testid="add-student-button">
          <Plus className="h-4 w-4 mr-1" /> {t("teach.add_student")}
        </Button>
      </div>

      <div className="relative max-w-sm">
        <Search className="h-4 w-4 t-secondary absolute left-3 top-1/2 -translate-y-1/2" />
        <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder={t("teach.search_student")} className="pl-9 bg-transparent border-app" data-testid="student-search-input" />
      </div>

      {filtered.length === 0 ? (
        <p className="text-sm t-secondary py-8 text-center" data-testid="students-empty">{t("teach.no_students")}</p>
      ) : (
        <div className="space-y-1.5" data-testid="students-list">
          {filtered.map((s) => (
            <button
              key={s.id}
              onClick={() => navigate(`/enseignement/eleves/${s.id}`)}
              className="w-full app-card px-4 py-3 flex items-center justify-between gap-3 text-left hover:border-app transition-colors"
              data-testid={`student-row-${s.id}`}
            >
              <div className="min-w-0">
                <p className="text-sm font-semibold truncate">
                  {s.prenom} {s.nom}
                  {s.account_status === "PENDING" && (
                    <span className="ml-2 text-[10px] font-semibold px-2 py-0.5 rounded-full bg-amber-500/15 text-amber-400 border border-amber-500/30">{t("teach.pending_account")}</span>
                  )}
                  {s.status === "INACTIVE" && (
                    <span className="ml-2 text-[10px] font-semibold px-2 py-0.5 rounded-full bg-rose-500/15 text-rose-400 border border-rose-500/30">{t("teach.inactive_relation")}</span>
                  )}
                </p>
                <p className="text-xs t-secondary truncate">{s.instrument}{s.level ? ` — ${s.level}` : ""}</p>
              </div>
              <div className="flex gap-1.5 flex-wrap justify-end shrink-0">
                {s.classes.map((c) => (
                  <span key={c.id} className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-sky-500/15 text-sky-400 border border-sky-500/30">{c.name}</span>
                ))}
              </div>
            </button>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="card-bg border-app" data-testid="add-student-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">{t("teach.add_student")}</DialogTitle>
            <DialogDescription className="t-secondary">{t("teach.add_student_desc")}</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="t-secondary text-xs uppercase tracking-wider">{t("firstname")}</Label>
                <Input value={form.prenom} onChange={(e) => setForm({ ...form, prenom: e.target.value })} className="mt-1 bg-transparent border-app" data-testid="student-prenom-input" autoFocus />
              </div>
              <div>
                <Label className="t-secondary text-xs uppercase tracking-wider">{t("lastname")}</Label>
                <Input value={form.nom} onChange={(e) => setForm({ ...form, nom: e.target.value })} className="mt-1 bg-transparent border-app" data-testid="student-nom-input" />
              </div>
            </div>
            <div>
              <Label className="t-secondary text-xs uppercase tracking-wider">{t("param.instrument_one")}</Label>
              <Input value={form.instrument} onChange={(e) => setForm({ ...form, instrument: e.target.value })} className="mt-1 bg-transparent border-app" data-testid="student-instrument-input" />
            </div>
            <div>
              <Label className="t-secondary text-xs uppercase tracking-wider">{t("teach.level")}</Label>
              <Input value={form.level} onChange={(e) => setForm({ ...form, level: e.target.value })} className="mt-1 bg-transparent border-app" data-testid="student-level-input" />
            </div>
            <Button className="w-full brand-bg text-white" onClick={create} disabled={!form.prenom.trim() || saving} data-testid="create-student-confirm">
              {t("common.create")}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
